import {
  Building2, CircleCheck, CircleX, Download, KeyRound, Link2, LoaderCircle, LogOut,
  Pencil, Plus, RefreshCw, ShieldCheck, Trash2, UserRoundPlus,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import type { InstrumentDto } from '../../shared/types';
import { api } from '../api';
import { AssetAvatar } from '../components/AssetAvatar';
import { HoldingGroupsSettings } from '../components/HoldingGroupsSettings';
import { InstrumentForm } from '../components/InstrumentForm';
import { kindLabel } from '../format';

type User = { id: string; displayName: string; role: string; lineSubject: string };
type Account = {
  id: string;
  name: string;
  type: string;
  institution: string | null;
  defaultCurrency: string;
  interestRatePct: string | null;
};
type Member = { id: string; displayName: string; role: string; active: boolean; createdAt: number };
type ProviderTestStatus = 'OK' | 'FAILED' | 'NOT_CONFIGURED' | 'ENTITLEMENT_REQUIRED' | 'RATE_LIMITED' | 'TIMEOUT';
type Provider = { id: string; label: string; configured: boolean; status?: ProviderTestStatus; checkedAt?: string; detail?: string };
type DemoStatus = { activeEvents: number; artifactCount: number };
type DemoCleanup = { removedEvents: number; removedSnapshots: number; removedTargets: number; removedQuotes: number };

function providerStatusText(provider: Provider) {
  const detail = provider.detail ? ` · ${provider.detail}` : '';
  if (provider.status === 'OK') return `เชื่อมต่อสำเร็จ${detail}`;
  if (provider.status === 'ENTITLEMENT_REQUIRED') return `API key ใช้ได้ แต่แพ็กเกจไม่รองรับตลาดนี้${detail}`;
  if (provider.status === 'RATE_LIMITED') return `ถูกจำกัดจำนวนคำขอชั่วคราว${detail}`;
  if (provider.status === 'TIMEOUT') return `ผู้ให้บริการตอบช้าเกินกำหนด${detail}`;
  if (provider.status === 'FAILED') return `เชื่อมต่อไม่สำเร็จ${detail}`;
  if (provider.status === 'NOT_CONFIGURED') return provider.detail || 'ยังไม่ได้ตั้งค่า API key';
  if (provider.configured) return `พร้อมทดสอบ${detail}`;
  return provider.detail || 'ยังไม่ได้ตั้งค่า API key';
}

export function SettingsPage({ user, reloadSummary }: { user: User; reloadSummary: () => Promise<void> }) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [instruments, setInstruments] = useState<InstrumentDto[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [testingProviders, setTestingProviders] = useState(false);
  const [invite, setInvite] = useState<string | null>(null);
  const [addingAccount, setAddingAccount] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [addingAsset, setAddingAsset] = useState(false);
  const [demoStatus, setDemoStatus] = useState<DemoStatus>({ activeEvents: 0, artifactCount: 0 });
  const [clearingDemo, setClearingDemo] = useState(false);

  async function load() {
    const [a, i, m, p, demo] = await Promise.all([
      api<Account[]>('/accounts'),
      api<InstrumentDto[]>('/instruments'),
      user.role === 'ADMIN' ? api<Member[]>('/admin/users') : Promise.resolve([]),
      api<Provider[]>('/prices/providers'),
      user.role === 'ADMIN' ? api<DemoStatus>('/admin/demo-portfolio') : Promise.resolve({ activeEvents: 0, artifactCount: 0 }),
    ]);
    setAccounts(a);
    setInstruments(i);
    setMembers(m);
    setProviders(p);
    setDemoStatus(demo);
  }

  useEffect(() => { void load(); }, []);

  async function createInvite() {
    const result = await api<{ url: string }>('/admin/invitations', { method: 'POST' });
    setInvite(result.url);
  }
  async function signOut() {
    await api('/auth/logout', { method: 'POST' });
    location.reload();
  }
  async function deleteEverything() {
    const typed = window.prompt('พิมพ์ DELETE เพื่อยืนยันการลบข้อมูลทั้งหมด');
    if (typed !== 'DELETE') return;
    await api('/me/data', { method: 'DELETE' });
    location.reload();
  }
  async function toggleMember(member: Member) {
    const updated = await api<Member>(`/admin/users/${member.id}/status`, {
      method: 'PATCH', body: JSON.stringify({ active: !member.active }),
    });
    setMembers((rows) => rows.map((row) => row.id === member.id ? { ...row, active: updated.active } : row));
  }
  async function testProviders() {
    setTestingProviders(true);
    try {
      setProviders(await api<Provider[]>('/prices/providers/test', { method: 'POST' }));
    } finally {
      setTestingProviders(false);
    }
  }
  async function clearDemoPortfolio() {
    if ((!demoStatus.activeEvents && !demoStatus.artifactCount)
      || !window.confirm('ล้างข้อมูลตัวอย่างออกจากพอร์ต?\n\nสลิปและรายการจริงจะไม่ถูกลบ บัญชีและรายการสินทรัพย์จะยังอยู่')) return;
    setClearingDemo(true);
    try {
      const result = await api<DemoCleanup>('/admin/demo-portfolio', { method: 'DELETE' });
      setDemoStatus({ activeEvents: 0, artifactCount: 0 });
      window.alert(`ล้างข้อมูลตัวอย่างแล้ว ${result.removedEvents} รายการ`);
      location.assign('/');
    } finally {
      setClearingDemo(false);
    }
  }
  async function accountSaved() {
    setAddingAccount(false);
    setEditingAccount(null);
    await load();
    await reloadSummary();
  }

  return <>
    <section className="page-heading">
      <div><div className="eyebrow">PREFERENCES</div><h1>ตั้งค่าพอร์ต</h1><p>จัดการบัญชี สินทรัพย์ สมาชิก และความเป็นส่วนตัว</p></div>
    </section>
    <HoldingGroupsSettings instruments={instruments} onSaved={reloadSummary} />
    <div className="settings-grid">
      <section className="panel settings-section">
        <div className="panel-head">
          <div><span className="panel-kicker">ACCOUNTS</span><h2>บัญชีและ Wallet</h2></div>
          <button className="button tertiary small" onClick={() => { setAddingAccount(true); setEditingAccount(null); }}><Plus size={15} /> เพิ่ม</button>
        </div>
        <div className="settings-list">
          {accounts.map((account) => <div key={account.id}>
            <span className="settings-icon"><Building2 /></span>
            <span>
              <strong>{account.name}</strong>
              <small>{account.institution || account.type} · {account.defaultCurrency}{account.type === 'BANK' && account.interestRatePct != null ? ` · ${Number(account.interestRatePct).toLocaleString('th-TH')}% ต่อปี` : ''}</small>
            </span>
            <button className="icon-button" onClick={() => { setEditingAccount(account); setAddingAccount(false); }} aria-label={`แก้ไขบัญชี ${account.name}`}><Pencil size={14} /></button>
          </div>)}
        </div>
        {(addingAccount || editingAccount) && <InlineAccountForm
          account={editingAccount || undefined}
          close={() => { setAddingAccount(false); setEditingAccount(null); }}
          saved={accountSaved}
        />}
      </section>

      <section className="panel settings-section">
        <div className="panel-head">
          <div><span className="panel-kicker">INSTRUMENTS</span><h2>รายการสินทรัพย์</h2></div>
          <button className="button tertiary small" onClick={() => setAddingAsset(true)}><Plus size={15} /> เพิ่ม</button>
        </div>
        <div className="settings-list instruments-list">
          {instruments.filter((item) => item.kind !== 'FIAT').map((instrument) => <div key={instrument.id}>
            <AssetAvatar instrument={instrument} />
            <span><strong>{instrument.symbol} · {instrument.name}</strong><small>{kindLabel[instrument.kind]} {instrument.market ? `· ${instrument.market}` : ''}</small></span>
          </div>)}
        </div>
        {addingAsset && <InstrumentForm cancel={() => setAddingAsset(false)} saved={async () => { setAddingAsset(false); await load(); }} />}
      </section>

      <section className="panel settings-section">
        <div className="panel-head"><div><span className="panel-kicker">MARKET DATA</span><h2>แหล่งราคาปัจจุบัน</h2></div><RefreshCw /></div>
        <div className="settings-list">
          {providers.map((provider) => <div key={provider.id}>
            <span className="settings-icon">{provider.status === 'OK' ? <CircleCheck /> : provider.status && provider.status !== 'NOT_CONFIGURED' ? <CircleX /> : <KeyRound />}</span>
            <span><strong>{provider.label}</strong><small>{providerStatusText(provider)}</small></span>
          </div>)}
        </div>
        {user.role === 'ADMIN' && <button className="button secondary full" disabled={testingProviders} onClick={testProviders}>
          {testingProviders ? <LoaderCircle className="spin" size={17} /> : <RefreshCw size={17} />} ทดสอบ Provider
        </button>}
      </section>

      <section className="panel settings-section">
        <div className="panel-head"><div><span className="panel-kicker">ACCESS</span><h2>สมาชิกและ LINE</h2></div><ShieldCheck /></div>
        <div className="profile-setting"><span className="big-avatar">{user.displayName.slice(0, 1)}</span><span><strong>{user.displayName}</strong><small>{user.role === 'ADMIN' ? 'ผู้ดูแลระบบ' : 'สมาชิก'} · เชื่อมกับ LINE แล้ว</small></span></div>
        {user.role === 'ADMIN' && <>
          <div className="settings-list member-list">
            {members.filter((member) => member.id !== user.id).map((member) => <div key={member.id}>
              <span className="big-avatar member-avatar">{member.displayName.slice(0, 1)}</span>
              <span><strong>{member.displayName}</strong><small>{member.active ? 'ใช้งานได้' : 'ระงับบัญชี'} · ไม่สามารถดูพอร์ตสมาชิก</small></span>
              <button className={`button small ${member.active ? 'danger-text' : 'tertiary'}`} onClick={() => toggleMember(member)}>{member.active ? 'ระงับ' : 'เปิดใช้'}</button>
            </div>)}
          </div>
          <button className="button secondary full" onClick={createInvite}><UserRoundPlus size={17} /> สร้างลิงก์เชิญ 24 ชั่วโมง</button>
          {invite && <div className="invite-box"><Link2 /><input readOnly value={invite} /><button className="button tertiary small" onClick={() => navigator.clipboard.writeText(invite)}>คัดลอก</button></div>}
        </>}
      </section>

      <section className="panel settings-section">
        <div className="panel-head"><div><span className="panel-kicker">YOUR DATA</span><h2>ส่งออกและความเป็นส่วนตัว</h2></div><KeyRound /></div>
        <p className="settings-copy">รูปสลิปและ raw OCR ถูกลบทันทีหลังยืนยัน คุณดาวน์โหลด ledger ทั้งหมดได้ตลอดเวลา</p>
        {(demoStatus.activeEvents > 0 || demoStatus.artifactCount > 0) && <div className="demo-cleanup-card">
          <span><strong>{demoStatus.activeEvents > 0 ? `พบข้อมูลตัวอย่าง ${demoStatus.activeEvents} รายการ` : 'พบภาพรวมเดโมที่ยังตกค้าง'}</strong><small>ล้างเฉพาะรายการ กราฟ ราคา และเป้าหมายเดโม โดยไม่แตะข้อมูลจริง</small></span>
          <button className="button danger-text full" disabled={clearingDemo} onClick={clearDemoPortfolio}><Trash2 size={17} /> {clearingDemo ? 'กำลังล้าง…' : 'ล้างข้อมูลตัวอย่าง'}</button>
        </div>}
        <a href="/api/export" className="button secondary full"><Download size={17} /> ดาวน์โหลด JSON</a>
        <button className="button tertiary full" onClick={signOut}><LogOut size={17} /> ออกจากระบบ</button>
        <button className="button danger-text full" onClick={deleteEverything}><Trash2 size={17} /> ลบบัญชีและข้อมูลทั้งหมด</button>
      </section>
    </div>
  </>;
}

function InlineAccountForm({ account, close, saved }: { account?: Account; close: () => void; saved: () => Promise<void> }) {
  const [form, setForm] = useState({
    name: account?.name || '',
    type: account?.type || 'BROKERAGE',
    institution: account?.institution || '',
    defaultCurrency: account?.defaultCurrency || 'USD',
    interestRatePct: account?.interestRatePct || '',
  });
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const payload = {
      ...form,
      interestRatePct: form.type === 'BANK' ? form.interestRatePct || null : null,
    };
    await api(account ? `/accounts/${account.id}` : '/accounts', {
      method: account ? 'PATCH' : 'POST', body: JSON.stringify(payload),
    });
    await saved();
  }
  return <form className="inline-form" onSubmit={submit}>
    <input placeholder="ชื่อบัญชี" required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
    <select value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value })}>
      <option value="BROKERAGE">Brokerage</option><option value="BANK">ธนาคาร</option><option value="GOLD">ทองคำ</option><option value="CRYPTO_WALLET">Crypto wallet</option>
    </select>
    <input placeholder="ธนาคาร / สถาบัน" value={form.institution} onChange={(event) => setForm({ ...form, institution: event.target.value })} />
    <select value={form.defaultCurrency} onChange={(event) => setForm({ ...form, defaultCurrency: event.target.value })}><option>USD</option><option>THB</option></select>
    {form.type === 'BANK' && <input type="number" min="0" max="100" step="0.01" inputMode="decimal" aria-label="อัตราดอกเบี้ยต่อปี (%)" placeholder="อัตราดอกเบี้ย % ต่อปี" value={form.interestRatePct} onChange={(event) => setForm({ ...form, interestRatePct: event.target.value })} />}
    <div><button type="button" className="button tertiary small" onClick={close}>ยกเลิก</button><button className="button primary small">{account ? 'บันทึกการแก้ไข' : 'บันทึก'}</button></div>
  </form>;
}
